import type { HumanProportions } from './human.js';

/** Multipliers on the shared humanoid frame, before muscle and fat. A neutral morph preserves the authored body. */
export interface HumanoidBodyMorph {
  breadth: number; depth: number; limbWidth: number;
  armLength: number; legLength: number; torsoLength: number; neckLength: number;
  headSize: number; handSize: number;
}
export const HUMANOID_BODY_NEUTRAL: HumanoidBodyMorph = {
  breadth: 1, depth: 1, limbWidth: 1, armLength: 1, legLength: 1, torsoLength: 1, neckLength: 1, headSize: 1, handSize: 1,
};
export const HUMANOID_BODY_FIELDS: { key: keyof HumanoidBodyMorph; label: string; min: number; max: number }[] = [
  { key: 'breadth', label: 'Body breadth', min: .75, max: 1.45 }, { key: 'depth', label: 'Body depth', min: .75, max: 1.4 },
  { key: 'limbWidth', label: 'Limb thickness', min: .7, max: 1.45 }, { key: 'armLength', label: 'Arm length', min: .8, max: 1.2 },
  { key: 'legLength', label: 'Leg length', min: .65, max: 1.2 }, { key: 'torsoLength', label: 'Torso length', min: .85, max: 1.15 },
  { key: 'neckLength', label: 'Neck length', min: .8, max: 1.4 }, { key: 'headSize', label: 'Head size', min: .85, max: 1.15 },
  { key: 'handSize', label: 'Hand size', min: .8, max: 1.35 },
];

/** Length changes move connected joints together; skin, equipment, IK and head preparation receive the same resolved frame. */
export function morphHumanoidBody(source: HumanProportions): HumanProportions {
  if (!source.bodyMorph) return source;
  const m = { ...HUMANOID_BODY_NEUTRAL, ...source.bodyMorph };
  if (Object.values(m).every(value => value === 1)) return source;
  const p = structuredClone(source);
  for (const key of ['hipHalfWidth', 'shoulderHalfWidth', 'chestHalfWidth', 'ribHalfWidth', 'waistHalfWidth', 'pelvisHalfWidth', 'socketX'] as const) p[key] *= m.breadth;
  for (const key of ['waistDepth', 'pelvisDepth', 'glute', 'socketZ', 'neckZ', 'headZ'] as const) p[key] *= m.depth;
  p.trunk = { front: p.trunk.front.map(value => value * m.depth), back: p.trunk.back.map(value => value * m.depth) };
  p.neck = { front: p.neck.front.map(value => value * m.depth), back: p.neck.back.map(value => value * m.depth) };
  p.limbThickness *= m.limbWidth; p.thighFullness *= m.limbWidth; p.neckRadius *= Math.sqrt(m.breadth * m.depth);
  p.bust *= m.depth; p.bustShape.width *= m.breadth; p.bustShape.outerWidth *= m.breadth;
  // The pelvis moulding follows the frame: hip flare and the gusset with breadth, seat and front with depth.
  const pelvis = p.pelvisShape; pelvis.flare *= m.breadth; pelvis.gusset *= m.breadth; pelvis.front *= m.depth; pelvis.gluteFold *= m.depth; pelvis.innerThigh *= m.limbWidth; pelvis.thighBack *= m.depth;
  p.upperArm *= m.armLength; p.forearm *= m.armLength;
  const legRise = (p.thigh + p.shin) * (m.legLength - 1);
  p.thigh *= m.legLength; p.shin *= m.legLength;
  p.pelvisShape.crotchDrop *= m.legLength; p.pelvisShape.gluteHeight *= m.torsoLength;
  const torsoRise = (p.shoulderY - p.hipsY) * (m.torsoLength - 1);
  p.torsoY += legRise + (p.torsoY - p.hipsY) * (m.torsoLength - 1);
  p.hipsY += legRise; p.shoulderY += legRise + torsoRise; p.neckY += legRise + torsoRise;
  const neckRise = (p.headY - source.neckY) * (m.neckLength - 1);
  p.headY += legRise + torsoRise + neckRise;
  p.headScale *= m.headSize; p.handScale *= m.handSize;
  // Keep the requested actor height meaningful as body proportions change.
  p.referenceHeight += legRise + torsoRise + neckRise + .24 * source.headScale * (m.headSize - 1);
  return p;
}
